import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import type { IStatusPanelParams } from 'ag-grid-community';

import { type GridTotalStore, noStore } from './total-store';

export type GridStatusBarParams = {
  entityLabel?: string;
  showTotal?: boolean;
  totalStore?: GridTotalStore;
};

export type GridStatusBarProps = IStatusPanelParams & GridStatusBarParams;

const getGridTotalCount = (api: IStatusPanelParams['api']) =>
  api.getGridOption('rowModelType') === 'clientSide'
    ? (api.getGridOption('rowData')?.length ?? 0)
    : (api.getServerSideGroupLevelState?.()?.[0]?.rowCount ?? 0);

const getSelectedCount = (
  api: IStatusPanelParams['api'],
  totalCount: number,
) => {
  try {
    const selectionState = api.getServerSideSelectionState();

    return selectionState &&
      'selectAll' in selectionState &&
      selectionState.selectAll
      ? totalCount - (selectionState.toggledNodes?.length ?? 0)
      : (selectionState?.toggledNodes?.length ?? api.getSelectedRows().length);
  } catch {
    return api.getSelectedRows().length;
  }
};

export function GridStatusBar({
  api,
  entityLabel = 'rows',
  showTotal = true,
  totalStore,
}: GridStatusBarProps) {
  const showSelected = api.getGridOption('rowSelection') != null;
  const initialGridCount = getGridTotalCount(api);
  const [gridCount, setGridCount] = useState(initialGridCount);
  const store = totalStore ?? noStore;
  const externalTotal = useSyncExternalStore(
    store.subscribe,
    store.get,
    store.get,
  );
  const total = totalStore ? externalTotal : gridCount;
  const totalCount = typeof total === 'object' && total ? total.count : total;
  const totalCapped = typeof total === 'object' && total ? total.capped : false;
  const totalVisible = showTotal && total !== undefined;
  const [selectedCount, setSelectedCount] = useState(() =>
    getSelectedCount(api, initialGridCount),
  );
  const totalCountRef = useRef(0);

  useEffect(() => {
    const updateTotal = () => {
      setGridCount(getGridTotalCount(api));
    };

    const updateSelected = () => {
      setSelectedCount(
        getSelectedCount(
          api,
          totalStore ? totalCountRef.current : getGridTotalCount(api),
        ),
      );
    };

    updateTotal();
    updateSelected();

    api.addEventListener('storeUpdated', updateTotal);
    api.addEventListener('modelUpdated', updateTotal);
    api.addEventListener('selectionChanged', updateSelected);

    return () => {
      api.removeEventListener('storeUpdated', updateTotal);
      api.removeEventListener('modelUpdated', updateTotal);
      api.removeEventListener('selectionChanged', updateSelected);
    };
  }, [api, totalStore]);

  useEffect(() => {
    totalCountRef.current = totalCount ?? 0;
  }, [totalCount]);

  return (
    <div className="flex items-center gap-4 px-3 py-1 text-sm text-gray-600 dark:text-gray-400">
      {totalVisible && (
        <span>
          <span className="font-semibold">Total:</span>{' '}
          <span className="tabular-nums">
            {totalCount == null
              ? '…'
              : `${totalCount}${totalCapped ? '+' : ''}`}
          </span>{' '}
          {entityLabel}
        </span>
      )}
      {totalVisible && showSelected && (
        <span className="text-gray-300 select-none dark:text-gray-600">|</span>
      )}
      {showSelected && (
        <span>
          <span className="font-semibold">Selected:</span>{' '}
          <span className="tabular-nums">{selectedCount}</span>
        </span>
      )}
    </div>
  );
}
